import {FC, useContext, useMemo} from "react";
import dayjs from "dayjs";
import {useQuery} from "@apollo/client";

import {Loading} from "../../../components/loading";
import {CompanyAnalyticsContext} from ".";
import {ChartCard, ChartLine} from "../chart-card";
import {AnalyticsCards} from "../card";
import {PostStats, Stat} from "./post-stats";
import {MultiColumn} from "../../../components/input";
import {useCustomRange} from "../../../components/input/date-time/date-range";
import {
	GET_COMPANY_LINKEDIN_ANALYTICS,
	GET_COMPANY_LINKEDIN_ANALYTICS_CARDS,
	ShareEventAnalytics,
	loadAnalytics,
} from "../../../data/analytics";

import styles from "./company.module.scss";

const keys = ["pageViews", "uniqueVisitors", "followers", "reactions", "clicks", "comments"] as const;
type Keys = typeof keys[number];

const stats: Stat[] = [
	{
		label: "Views",
		value: "views",
	},
	{
		label: "Clicks",
		value: "clicks",
	},
	{
		label: "Reactions",
		value: "reactions",
	},
	{
		label: "Comments",
		value: "comments",
	},
	{
		label: "Reposts",
		value: "reposts",
	},
	{
		label: "Engagement Rate",
		format: v => `${Math.round(v * 100)}%`,
		value: "engagement",
	},
];

export const LinkedIn: FC = () => {
	const {accounts, dateRange} = useContext(CompanyAnalyticsContext);
	const {start, end} = useCustomRange(dateRange, dayjs().subtract(13, "month"));
	const {data: rawData, loading: companyAnalyticsLoading} = useQuery(GET_COMPANY_LINKEDIN_ANALYTICS, {
		variables: {ids: accounts.length ? accounts : undefined, start, end},
	});
	const {data: cardData, loading} = useQuery(GET_COMPANY_LINKEDIN_ANALYTICS_CARDS, {
		variables: {ids: accounts.length ? accounts : undefined, start, end},
	});
	const [labels, shareEvents, shareData, visitorData, followerData] = useMemo<
		[string[], ShareEventAnalytics[], ChartLine<string>[], ChartLine<string>[], ChartLine<string>[]]
	>(() => {
		const labels: string[] = [];
		const raw = rawData?.companyLinkedInAnalytics ?? {};
		const data: Record<number, ShareEventAnalytics> = {};
		Object.keys(raw).forEach(id => (data[id] = loadAnalytics(raw[id])));
		const shareEvents = Object.keys(data).reduce(
			(acc, item) => [...acc, ...data[item].shareEvents],
			[] as ShareEventAnalytics[]
		);

		const aggregate = {} as Record<Keys, number[]>;
		let day = start;
		let i = 0;
		keys.forEach(k => (aggregate[k] = []));
		while (day.isBefore(end)) {
			if (start.isSame(end, "year")) {
				labels.push(day.format("MMM DD"));
			} else {
				labels.push(day.format("MM/DD/YYYY"));
			}
			keys.forEach(k => {
				const value = Object.keys(data).reduce((acc, c) => acc + data[c][k][i], 0);
				aggregate[k].push(value);
			});
			day = day.add(1, "day");
			i++;
		}
		return [
			labels,
			shareEvents,
			[
				{label: "Reactions", value: "reactions", color: "pink-primary", data: aggregate.reactions},
				{label: "Comments", value: "comments", color: "blue-primary", data: aggregate.comments},
				{label: "Clicks", value: "clicks", color: "pink-100", data: aggregate.clicks},
			],
			[
				{label: "Page Views", value: "views", color: "green", data: aggregate.pageViews},
				{label: "Unique Visitors", value: "visitors", color: "blue-primary", data: aggregate.uniqueVisitors},
			],
			[{label: "Page Followers", value: "followers", color: "blue-primary", data: aggregate.followers}],
		];
	}, [rawData, start, end]);
	return (
		<div className={styles.container}>
			{loading ? <Loading /> : <AnalyticsCards cards={cardData?.companyLinkedInAnalyticsCards ?? []} />}
			<ChartCard
				labels={labels}
				title="LinkedIn Post Engagement"
				data={shareData}
				allLabel="Engagements"
				loading={companyAnalyticsLoading}
			/>
			<MultiColumn>
				<ChartCard
					labels={labels}
					title="LinkedIn Page Visitor Highlights"
					data={visitorData}
					allLabel="Visitors"
					select={false}
					loading={companyAnalyticsLoading}
				/>
				<ChartCard
					labels={labels}
					title="LinkedIn Page Total Followers"
					data={followerData}
					allLabel="Visitors"
					select={false}
					loading={companyAnalyticsLoading}
				/>
			</MultiColumn>
			<PostStats
				shareEvents={shareEvents}
				account="linkedin"
				stats={stats}
				loading={companyAnalyticsLoading}
			/>
		</div>
	);
};
